import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useSubscription } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useDevicesAndChannels } from '.';
import { useInstallationContext } from '../../../../context/installation';
import {
  DevicesGetAllQuery,
  DevicesGetAllQueryVariables,
  OnConfigurationTaintedSubscription,
  OnConfigurationTaintedSubscriptionVariables,
  OnDeviceNameChangedSubscription,
  OnDeviceNameChangedSubscriptionVariables,
  UserDevice,
} from '../../../../data-access/gql-types/graphql';
import { DEVICES_GET_ALL } from '../../../../data-access/queries/devices';
import {
  ON_CONFIGURATION_TAINTED,
  ON_DEVICE_CONNECTION_STATE_CHANGE,
} from '../../../../data-access/subscriptions/lavva-devices';
import { ON_DEVICE_NAME_CHANGED } from '../../../../data-access/subscriptions/on-device-name-changed';
import { convertErrorsToOneString, isLocalApp } from '../../../../utils/helpers';
import { isDevelopment } from '../../../../utils/helpers/environment';
import { toastError, toastSuccess } from '../../../../utils/toast';
import { useDevicesStore } from '../../store/devices';
import { DeviceType } from '../../types';
import { useUpdateCommonLavvaState } from '../../utils/channels/subscriptions/common-lavva-state';
import { parseDevice } from '../../utils/devices';

export const useFetchDevices = () => {
  const { t } = useTranslation('channel-settings');
  const { selectedInstallationId, skipLavvaFetch, currentInstallationLoading } = useInstallationContext();
  const { setDeviceList, editDeviceName, channelList } = useDevicesAndChannels();
  const { onDeviceConnectionStateChange } = useUpdateCommonLavvaState();
  const { shouldRefetchDevices, resetRefetchDevices, setDevicesLoading } = useDevicesStore((s) => s);

  const processDevicesData = (data: DevicesGetAllQuery) => {
    if (data?.allUserDevices && selectedInstallationId) {
      const updatedDevices: DeviceType[] = [];

      for (const userDevice of data.allUserDevices.filter(
        (x) => x.installationId === selectedInstallationId && x.payload,
      )) {
        const newDevice = parseDevice(userDevice as UserDevice);
        updatedDevices.push(newDevice);
      }

      setDeviceList(updatedDevices);
    }
  };

  const {
    data: devicesData,
    loading,
    refetch,
  } = useQuery<DevicesGetAllQuery, DevicesGetAllQueryVariables>(DEVICES_GET_ALL, {
    variables: { installationId: selectedInstallationId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: skipLavvaFetch,
  });

  useEffect(() => {
    if (devicesData) processDevicesData(devicesData);
  }, [devicesData]);

  useEffect(() => {
    if (currentInstallationLoading) setDevicesLoading(true);
    else {
      if (!selectedInstallationId) setDevicesLoading(false);
      else {
        setDevicesLoading(loading || skipLavvaFetch);
      }
    }
  }, [loading, skipLavvaFetch, currentInstallationLoading, selectedInstallationId]);

  const fetchDevices = async () => {
    resetRefetchDevices();

    try {
      const { data } = await refetch({ installationId: selectedInstallationId });

      if (data) processDevicesData(data);
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    if (shouldRefetchDevices && selectedInstallationId) fetchDevices();
  }, [shouldRefetchDevices, selectedInstallationId, fetchDevices]);

  const { data } = useSubscription<OnDeviceNameChangedSubscription, OnDeviceNameChangedSubscriptionVariables>(
    ON_DEVICE_NAME_CHANGED,
    {
      variables: { installationId: selectedInstallationId },
      skip: skipLavvaFetch,
    },
  );
  const { data: configurationTainted } = useSubscription<
    OnConfigurationTaintedSubscription,
    OnConfigurationTaintedSubscriptionVariables
  >(ON_CONFIGURATION_TAINTED, {
    variables: { installationId: selectedInstallationId },
    skip: skipLavvaFetch,
  });

  const { data: deviceConnection } = useSubscription(ON_DEVICE_CONNECTION_STATE_CHANGE, {
    variables: { installationId: selectedInstallationId },
    skip: skipLavvaFetch,
  });

  useEffect(() => {
    if (deviceConnection) onDeviceConnectionStateChange(deviceConnection.onDeviceConnectionStateChange);
  }, [deviceConnection]);

  useEffect(() => {
    if (data?.onDeviceNameChanged) {
      const { deviceId, name } = data.onDeviceNameChanged;
      editDeviceName(deviceId, name);
      toastSuccess({ content: t('toast.editNameSuccess') });
    }
  }, [data]);

  useEffect(() => {
    if (configurationTainted?.onConfigurationTaintedPayload) {
      const { channelId, failures } = configurationTainted.onConfigurationTaintedPayload;
      const channel = channelList.find((x) => x.id === channelId);
      console.log('CONFIGURATION TAINTED', { channelId, failures });

      if (!isLocalApp) {
        Sentry.captureMessage('Configuration tainted', {
          level: 'info',
          extra: {
            channelId,
            failures,
          },
        });
      }

      if (isDevelopment) {
        toastError({
          content: `${t('configurationTainted', { alias: channel?.alias })}: \r\n${convertErrorsToOneString(failures)}`,
        });
      }
    }
  }, [configurationTainted]);
};
